<template>
  <transition name="gs-fade">
    <div v-if="open" class="gs-overlay" @mousedown.self="close">
      <transition name="gs-slide">
        <div v-if="open" class="gs-box" :class="isDark ? 'gs-dark' : 'gs-light'">

          <!-- ── Input row ── -->
          <div class="gs-input-row">

            <!-- Param mode: icon + breadcrumb + (optional search_by select) + param field -->
            <template v-if="mode === 'param'">
              <i :class="paramPage.icon" class="gs-icon" />
              <span class="gs-breadcrumb">{{ paramPage.text }}&nbsp;›&nbsp;</span>
              <select
                v-if="paramPage.searchByOptions"
                v-model="paramSearchBy"
                class="pal-by-select"
              >
                <option
                  v-for="opt in paramPage.searchByOptions"
                  :key="opt.value"
                  :value="opt.value"
                >{{ opt.text }}</option>
              </select>
              <input
                ref="paramInput"
                v-model="paramValue"
                class="gs-input"
                :placeholder="paramPage.searchHint || 'Buscar...'"
                autocomplete="off"
                @keydown.esc.prevent="exitParamMode"
                @keydown.enter.prevent="navigateWithParam"
              />
              <kbd class="gs-esc" style="cursor:pointer" @click="exitParamMode">Esc</kbd>
            </template>

            <!-- Client views mode -->
            <template v-else-if="mode === 'client-views'">
              <div class="gs-cv-avatar">{{ clientInitials(tabClient) }}</div>
              <span class="gs-breadcrumb">{{ tabClient.nombre || tabClient.username }}&nbsp;›&nbsp;</span>
              <input
                ref="cvInput"
                class="gs-input"
                readonly
                placeholder="Selecciona una vista…"
                @keydown.esc.prevent="exitClientViewsMode"
                @keydown.tab.prevent="exitClientViewsMode"
                @keydown.up.prevent="cvSel = (cvSel - 1 + clientViews.length) % clientViews.length"
                @keydown.down.prevent="cvSel = (cvSel + 1) % clientViews.length"
                @keydown.enter.prevent="navigateClientView(clientViews[cvSel])"
              />
              <kbd class="gs-esc" style="cursor:pointer" @click="exitClientViewsMode">Esc</kbd>
            </template>

            <!-- Browse / search mode -->
            <template v-else>
              <i class="mdi mdi-magnify gs-icon" />
              <input
                ref="gsInput"
                v-model="query"
                class="gs-input"
                placeholder="Buscar páginas, clientes, acciones..."
                autocomplete="off"
                @keydown.esc.prevent="close"
                @keydown.tab.prevent="onTab"
                @keydown.up.prevent="moveSel(-1)"
                @keydown.down.prevent="moveSel(1)"
                @keydown.enter.prevent="confirmSel"
              />
              <div v-if="loading" class="gs-spinner"><span/><span/><span/></div>
              <kbd v-else class="gs-esc">ESC</kbd>
            </template>
          </div>

          <!-- ── Client views results ── -->
          <div v-if="mode === 'client-views'" class="gs-results">
            <div class="gs-section-label">Ir a vista de cliente</div>
            <button
              v-for="(v, i) in clientViews"
              :key="i"
              class="gs-result"
              :class="{ 'gs-result--sel': cvSel === i }"
              @mouseenter="cvSel = i"
              @click="navigateClientView(v)"
            >
              <i :class="v.icon" class="gs-r-icon" />
              <span class="gs-r-title">{{ v.text }}</span>
            </button>
          </div>

          <!-- ── Browse / search results ── -->
          <div v-if="mode === 'browse'" class="gs-results" ref="resultsList">

            <!-- PÁGINAS -->
            <template v-if="visiblePages.length">
              <div class="gs-section-label">Páginas</div>
              <button
                v-for="(p, i) in visiblePages"
                :key="'p' + i"
                class="gs-result"
                :class="{ 'gs-result--sel': flatSel === pageOffset + i }"
                @mouseenter="flatSel = pageOffset + i"
                @click="navigatePage(p)"
              >
                <i :class="p.icon" class="gs-r-icon" />
                <span class="gs-r-title">{{ p.text }}</span>
                <span class="gs-r-module">{{ p.module }}</span>
                <span
                  v-if="p.searchParam && flatSel === pageOffset + i"
                  class="gs-tab-hint"
                ><kbd>Tab</kbd> buscar dentro</span>
              </button>
            </template>

            <!-- ACCIONES -->
            <template v-if="visibleActions.length">
              <div class="gs-section-label">Acciones</div>
              <button
                v-for="(a, i) in visibleActions"
                :key="'a' + i"
                class="gs-result"
                :class="{ 'gs-result--sel': flatSel === actionOffset + i }"
                @mouseenter="flatSel = actionOffset + i"
                @click="triggerAction(a)"
              >
                <i :class="a.icon" class="gs-r-icon" />
                <span class="gs-r-title">{{ a.text }}</span>
                <span class="gs-r-module">{{ a.module }}</span>
              </button>
            </template>

            <!-- CLIENTES -->
            <template v-if="query.length >= 2">
              <div class="gs-section-label">
                Clientes
                <span v-if="clientsLoading" class="gs-clients-loading">
                  <span class="gs-dot-spin" /><span class="gs-dot-spin" /><span class="gs-dot-spin" />
                </span>
              </div>
              <template v-if="clients.length">
                <div
                  v-for="(c, i) in clients"
                  :key="c.id"
                  class="gs-result gs-result--client"
                  :class="{ 'gs-result--sel': flatSel === clientOffset + i }"
                  @mouseenter="flatSel = clientOffset + i"
                >
                  <div class="gs-client-avatar">{{ clientInitials(c) }}</div>
                  <div class="gs-client-info">
                    <span class="gs-r-title">
                      {{ c.nombre || c.username }}
                      <span
                        v-if="matchBadge(c)"
                        class="gs-match-badge"
                        :class="matchBadge(c).cls"
                      >{{ matchBadge(c).text }}</span>
                    </span>
                    <span class="gs-client-meta">{{ c.rut }} · {{ c.email }}</span>
                  </div>
                  <button
                    class="gs-ctx-btn"
                    :class="{ 'gs-ctx-btn--active': activeContextClient && activeContextClient.id === c.id }"
                    :title="activeContextClient && activeContextClient.id === c.id ? 'Cliente en contexto activo' : 'Fijar contexto de cliente'"
                    @click.stop="setClientContext(c)"
                  >
                    <i :class="activeContextClient && activeContextClient.id === c.id ? 'mdi mdi-account-check' : 'mdi mdi-target-account'" />
                    {{ activeContextClient && activeContextClient.id === c.id ? 'En contexto' : 'Fijar contexto' }}
                  </button>
                </div>
              </template>
              <div v-else-if="!clientsLoading" class="gs-clients-empty">
                No se encontraron clientes para <em>"{{ query }}"</em>
              </div>
            </template>

            <!-- Empty (query typed but nothing found) -->
            <div
              v-if="query.length >= 2 && !visiblePages.length && !visibleActions.length && !clients.length && !clientsLoading"
              class="gs-empty"
            >
              <i class="mdi mdi-magnify" style="font-size:28px;opacity:0.25;margin-bottom:6px;" />
              <span>Sin resultados para <em>"{{ query }}"</em></span>
            </div>
          </div>

          <!-- ── Param mode body ── -->
          <div v-if="mode === 'param'" class="gs-param-body">
            <i :class="paramPage.icon" class="gs-param-icon" />
            <div class="gs-param-info">
              <span class="gs-param-name">{{ paramPage.text }}</span>
              <span class="gs-param-desc">
                Ingresa <em>{{ paramPage.searchHint }}</em> para ir directo al resultado, o presiona
                <kbd>↵</kbd> vacío para abrir la página.
              </span>
            </div>
          </div>

          <!-- ── Footer ── -->
          <div class="gs-hint">
            <template v-if="mode === 'param'">
              <span class="gs-hint-shortcuts">
                <kbd>Esc</kbd>&nbsp;volver
                &ensp;
                <kbd>↵</kbd>&nbsp;{{ paramPage.paramRoute ? (paramValue.trim() ? 'ir al ticket #' + paramValue.trim() : 'ir a ' + paramPage.text) : (paramValue.trim() ? 'buscar en ' + paramPage.text : 'ir a ' + paramPage.text) }}
              </span>
            </template>
            <template v-else-if="mode === 'client-views'">
              <span class="gs-hint-shortcuts">
                <kbd>Tab</kbd>&nbsp;volver
                &ensp;<kbd>↑↓</kbd>&nbsp;navegar
                &ensp;<kbd>↵</kbd>&nbsp;ir a vista
                &ensp;<kbd>Esc</kbd>&nbsp;volver
              </span>
            </template>
            <template v-else>
              <span class="gs-hint-shortcuts">
                <template v-if="currentSelected && currentSelected.searchParam">
                  <kbd>Tab</kbd>&nbsp;buscar dentro&ensp;
                </template>
                <template v-else-if="currentSelected && currentSelected.rut">
                  <kbd>Tab</kbd>&nbsp;ver vistas&ensp;
                </template>
                <kbd>↑↓</kbd>&nbsp;navegar
                &ensp;<kbd>↵</kbd>&nbsp;abrir
                &ensp;<kbd>ESC</kbd>&nbsp;cerrar
              </span>
            </template>
          </div>

        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { API_URLS_BOARD } from '@/constants';
import { buildClienteResumenRequestBody, extractClienteResumenItems, mapClienteResumenItem } from '@/helpers/clienteResumen';

const PAGES = [
  { icon: 'mdi mdi-view-dashboard-outline',    text: 'Dashboard',               link: '/',                                  module: 'Inicio' },
  { icon: 'mdi mdi-ticket-outline',            text: 'Tickets / Board',          link: '/apps/board',                        module: 'Kanban',        searchParam: 'id', searchHint: 'ID del ticket', paramRoute: (val) => val ? `/tickets/${val}` : '/apps/board' },
  // Oculto temporalmente (reversible: descomentar para restaurar el acceso al mantenedor "Clientes").
  // { icon: 'mdi mdi-account-tie',               text: 'Clientes',                 link: '/clients',                           module: 'Mantenedor',    searchParam: 'search', searchHint: 'RUT o nombre' },
  { icon: 'mdi mdi-account',                   text: 'Usuarios',                 link: '/users',                             module: 'Mantenedor',    searchParam: 'search', searchHint: 'Nombre o email' },
  { icon: 'mdi mdi-text-box-plus-outline',     text: 'Emitir facturas',          link: '/facturacion',                       module: 'Finanzas' },
  { icon: 'mdi mdi-text-box-plus-outline',     text: 'Emitir facturas crédito',  link: '/facturacion-credito',               module: 'Finanzas' },
  { icon: 'mdi mdi-text-box-check-outline',    text: 'Emitir cobros Facturas',   link: '/cobros',                            module: 'Finanzas' },
  { icon: 'mdi mdi-text-box-check-outline',    text: 'Emitir cobros Notas',      link: '/cobrar-notas',                      module: 'Finanzas' },
  { icon: 'mdi mdi-text-box-plus-outline',     text: 'Emitir notas de crédito',  link: '/facturacion-emision',               module: 'Finanzas' },
  { icon: 'mdi mdi-text-box-plus-outline',     text: 'Facturas emitidas',        link: '/facturas-emitidas',                 module: 'Finanzas' },
  { icon: 'mdi mdi-tag-text-outline',          text: 'Gestionar Proveedores',    link: '/facturacion-compra-providers',      module: 'Compras',          searchParam: 'search', searchByParam: 'search_by', searchByDefault: 'razon_social', searchByOptions: [{ text: 'Razón Social', value: 'razon_social' }, { text: 'Rut', value: 'rut' }], searchHint: 'Texto a buscar' },
  { icon: 'mdi mdi-bank-transfer',             text: 'Transferencias Masivas',   link: '/facturacion-compra-trasferencia',   module: 'Compras' },
  { icon: 'mdi mdi-note-outline',              text: 'Emitir Nota de cobro',     link: '/notas-cobro-generar',               module: 'Notas de cobro' },
  { icon: 'mdi mdi-note-text-outline',         text: 'Notas de cobro emitidas',  link: '/notas-cobros',                      module: 'Notas de cobro',   searchParam: 'search', searchByParam: 'search_by', searchByDefault: 'rut', searchByOptions: [{ text: 'ID', value: 'id' }, { text: 'Identificador', value: 'identificador' }, { text: 'Rut cliente', value: 'rut' }, { text: 'Username', value: 'username' }], searchHint: 'Valor a buscar' },
  { icon: 'mdi mdi-note-search-outline',       text: 'Notas no generadas',       link: '/notas-cobro/no-generadas',          module: 'Notas de cobro' },
  { icon: 'mdi mdi-bank-transfer',             text: 'Pagos Manuales', link: '/transferencia-electronica',      module: 'Finanzas' },
  { icon: 'mdi mdi-phone-forward',             text: 'Soporte telefónico',       link: '/sofono',                            module: 'Soporte' },
  { icon: 'mdi mdi-file-document-edit-outline',text: 'Cotizaciones',             link: '/cotizaciones',                      module: 'Comercial' },
  { icon: 'mdi mdi-account-multiple-check',    text: 'Roles y permisos',         link: '/roles',                             module: 'Mantenedor' },
  { icon: 'mdi mdi-ticket-percent',            text: 'Cupones',                  link: '/coupons',                           module: 'Mantenedor' },
  { icon: 'mdi mdi-layers-triple-outline',     text: 'Planes de Servicios',      link: '/planes-servicios',                  module: 'Mantenedor' },
  { icon: 'mdi mdi-tag-outline',               text: 'Categorías',               link: '/categorias',                        module: 'Mantenedor',    searchParam: 'search',       searchHint: 'Nombre de categoría' },
  { icon: 'mdi mdi-tag-multiple-outline',      text: 'Super Categorías',         link: '/categorias',                        module: 'Mantenedor',    searchParam: 'search_super', searchHint: 'Nombre de super categoría' },
  { icon: 'mdi mdi-check-circle-outline',      text: 'Habilitaciones',           link: '/apps/servicios',                    module: 'Servicios',     searchParam: 'search', searchHint: 'Dominio o servicio' },
  // Oculto a pedido del equipo: la entrada "Dashboard Cliente" no debe mostrarse en el buscador global (igual que en el menú lateral).
  // { icon: 'mdi mdi-dns',                       text: 'Dashboard Cliente',        link: '/dashboardClient_search',            module: 'Clientes',      searchParam: 'search', searchHint: 'RUT o email' },
  { icon: 'mdi mdi-ip-network-outline',        text: 'IP temporales',            link: '/acceso-ip-internacional-temporal',  module: 'Redes',         searchParam: 'search', searchHint: 'IP o cliente' },
  { icon: 'mdi mdi-email-outline',             text: 'SMTP temporal',            link: '/smtp-temporal',                     module: 'Redes' },
  { icon: 'mdi mdi-dns',                       text: 'SMTP poolname',            link: '/smtp-poolname',                     module: 'Redes' },
  { icon: 'mdi mdi-shield-lock-outline',       text: 'Gestión de políticas',     link: '/redes/gestion-politicas',           module: 'Redes' },
];

const ACTIONS = [
  { icon: 'mdi mdi-ticket-plus-outline', text: 'Crear ticket',               module: 'Acción',     action: 'create-ticket' },
  { icon: 'mdi mdi-brightness-4',        text: 'Cambiar tema',               module: 'Apariencia', action: 'toggle-theme'  },
  { icon: 'mdi mdi-account-off-outline', text: 'Salir del contexto cliente', module: 'Contexto',   action: 'clear-client-context', requiresContext: true },
];

const CLIENT_VIEWS = [
  { icon: 'mdi mdi-account-details-outline', text: 'Resumen Cliente',    route: (c) => ({ path: '/resumen-cliente',   query: { rut: c.rut } }) },
  { icon: 'mdi mdi-ticket-outline',          text: 'Tickets',             route: (c) => ({ path: '/apps/board',        query: { cliente: c.rut } }) },
  { icon: 'mdi mdi-note-text-outline',       text: 'Notas de Cobro',      route: (c) => ({ path: '/notas-cobros',      query: { search_by: 'rut', search: c.rut } }) },
  { icon: 'mdi mdi-check-circle-outline',    text: 'Habilitaciones',      route: (c) => ({ path: '/apps/servicios',    query: { search: c.rut, searchType: 'cliente_rut' } }) },
  { icon: 'mdi mdi-phone-forward',           text: 'Soporte Telefónico',  route: (c) => ({ path: '/sofono',            query: { cliente: c.rut } }) },
];

export default {
  name: 'GlobalSearch',

  props: {
    open:   { type: Boolean, default: false },
    isDark: { type: Boolean, default: false },
  },

  emits: ['close', 'open-ticket', 'action'],

  data: () => ({
    query:      '',
    flatSel:    0,
    _debounce:  null,
    // param-entry mode
    mode:         'browse',   // 'browse' | 'param' | 'client-views'
    paramPage:    null,
    paramValue:   '',
    paramSearchBy: null,
    // client-views mode
    tabClient: null,
    cvSel:     0,
    // client search
    clients:        [],
    clientsLoading: false,
    _clientDebounce: null,
  }),

  computed: {
    activeContextClient() {
      return this.$store.getters['clientContext/selectedClient'];
    },
    hasClientContext() {
      return this.$store.getters['clientContext/hasContext'];
    },
    visiblePages() {
      if (!this.query) return PAGES;
      const q = this.query.toLowerCase();
      return PAGES.filter(p =>
        p.text.toLowerCase().includes(q) ||
        p.module.toLowerCase().includes(q)
      );
    },
    visibleActions() {
      const applicable = ACTIONS.filter((a) => {
        if (a.requiresContext && !this.hasClientContext) return false;
        return true;
      });
      if (!this.query) return applicable;
      const q = this.query.toLowerCase();
      return applicable.filter(a =>
        a.text.toLowerCase().includes(q) ||
        a.module.toLowerCase().includes(q)
      );
    },
    pageOffset()   { return 0; },
    actionOffset() { return this.visiblePages.length; },
    clientOffset() { return this.visiblePages.length + this.visibleActions.length; },
    totalItems()   { return this.visiblePages.length + this.visibleActions.length + this.clients.length; },
    clientViews()  { return CLIENT_VIEWS; },
    currentSelected() {
      const i = this.flatSel;
      if (i < this.visiblePages.length)
        return this.visiblePages[i];
      const ai = i - this.actionOffset;
      if (ai >= 0 && ai < this.visibleActions.length)
        return this.visibleActions[ai];
      const ci = i - this.clientOffset;
      if (ci >= 0 && ci < this.clients.length)
        return this.clients[ci];
      return null;
    },
  },

  watch: {
    open(val) {
      if (val) {
        this.query     = '';
        this.flatSel   = 0;
        this.mode         = 'browse';
        this.paramPage    = null;
        this.paramValue   = '';
        this.paramSearchBy = null;
        this.tabClient    = null;
        this.cvSel        = 0;
        this.clients      = [];
        this.clientsLoading = false;
        this.$nextTick(() => this.$refs.gsInput && this.$refs.gsInput.focus());
      }
    },
    query(val) {
      this.flatSel = 0;
      clearTimeout(this._clientDebounce);
      if (val.length >= 2) {
        this.clientsLoading = true;
        this._clientDebounce = setTimeout(() => this.searchClients(val), 350);
      } else {
        this.clients = [];
        this.clientsLoading = false;
      }
    },
    flatSel() {
      // keep selection in bounds when lists shrink
      if (this.flatSel >= this.totalItems && this.totalItems > 0) {
        this.flatSel = this.totalItems - 1;
      }
    },
  },

  mounted() {
    window.addEventListener('keydown', this.onGlobalKey);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKey);
  },

  methods: {
    close() { this.$emit('close'); },

    onGlobalKey(e) {
      if (e.key === 'Escape' && this.open) {
        e.preventDefault();
        if (this.mode === 'param') this.exitParamMode();
        else if (this.mode === 'client-views') this.exitClientViewsMode();
        else this.close();
      }
    },

    moveSel(dir) {
      if (!this.totalItems) return;
      this.flatSel = (this.flatSel + dir + this.totalItems) % this.totalItems;
      this.$nextTick(() => {
        const list = this.$refs.resultsList;
        if (!list) return;
        const items = list.querySelectorAll('.gs-result');
        if (items[this.flatSel]) items[this.flatSel].scrollIntoView({ block: 'nearest' });
      });
    },

    confirmSel() {
      const item = this.currentSelected;
      if (!item) return;
      if (item.link)        this.navigatePage(item);
      else if (item.action) this.triggerAction(item);
      else if (item.id)     this.setClientContext(item);
    },

    onTab() {
      const item = this.currentSelected;
      if (!item) return;
      if (item.searchParam) this.enterParamMode(item);
      else if (item.rut)    this.enterClientViewsMode(item);
    },

    /* ── Page navigation ── */
    navigatePage(page, param) {
      const route = (param && page.searchParam)
        ? { path: page.link, query: { [page.searchParam]: param } }
        : page.link;
      this.$router.push(route).catch(() => {});
      this.close();
    },

    /* ── Param mode ── */
    enterParamMode(page) {
      this.mode         = 'param';
      this.paramPage    = page;
      this.paramValue   = '';
      this.paramSearchBy = page.searchByDefault
        || (page.searchByOptions && page.searchByOptions[0] && page.searchByOptions[0].value)
        || null;
      this.$nextTick(() => this.$refs.paramInput && this.$refs.paramInput.focus());
    },
    exitParamMode() {
      this.mode          = 'browse';
      this.paramPage     = null;
      this.paramValue    = '';
      this.paramSearchBy = null;
      this.$nextTick(() => this.$refs.gsInput && this.$refs.gsInput.focus());
    },
    /* ── Client views mode ── */
    enterClientViewsMode(client) {
      this.mode      = 'client-views';
      this.tabClient = client;
      this.cvSel     = 0;
      this.$nextTick(() => this.$refs.cvInput && this.$refs.cvInput.focus());
    },
    exitClientViewsMode() {
      this.mode      = 'browse';
      this.tabClient = null;
      this.cvSel     = 0;
      this.$nextTick(() => this.$refs.gsInput && this.$refs.gsInput.focus());
    },
    navigateClientView(view) {
      this.$store.dispatch('clientContext/setClient', {
        id: this.tabClient.id,
        nombre: this.tabClient.nombre,
        username: this.tabClient.username,
        rut: this.tabClient.rut,
        email: this.tabClient.email,
        facturacion_nombre: this.tabClient.facturacion_nombre || null,
        facturacion_rut: this.tabClient.facturacion_rut || this.tabClient.rut || null,
      });
      this.$router.push(view.route(this.tabClient)).catch(() => {});
      this.close();
    },

    navigateWithParam() {
      const val = this.paramValue.trim();
      if (this.paramPage.paramRoute) {
        this.$router.push(this.paramPage.paramRoute(val)).catch(() => {});
        this.close();
        return;
      }
      if (!val) {
        this.navigatePage(this.paramPage, null);
        return;
      }
      const query = { [this.paramPage.searchParam]: val };
      if (this.paramPage.searchByParam && this.paramSearchBy) {
        query[this.paramPage.searchByParam] = this.paramSearchBy;
      }
      this.$router.push({ path: this.paramPage.link, query }).catch(() => {});
      this.close();
    },

    /* ── Actions ── */
    triggerAction(a) {
      if (a.action === 'clear-client-context') {
        this.$store.dispatch('clientContext/clearClient');
        this.close();
        return;
      }
      this.$emit('action', a.action);
      this.close();
    },

    /* ── Client search & context ── */
    async searchClients(q) {
      try {
        const url = API_URLS_BOARD.baseUrl + API_URLS_BOARD.clienteResumen;
        const body = buildClienteResumenRequestBody({ search: q, page: 1, perPage: 6 });
        const res = await axios.post(url, body);
        const rawItems = extractClienteResumenItems(res.data || {});
        this.clients = rawItems.map((r) => mapClienteResumenItem(r)).filter(Boolean);
      } catch (_) {
        this.clients = [];
      } finally {
        this.clientsLoading = false;
      }
    },

    setClientContext(client) {
      this.$store.dispatch('clientContext/setClient', {
        id: client.id,
        nombre: client.nombre,
        username: client.username,
        rut: client.rut,
        email: client.email,
        facturacion_nombre: client.facturacion_nombre || null,
        facturacion_rut: client.facturacion_rut || client.rut || null,
      });
      const CONTEXT_AWARE = [
        '/apps/servicios', '/notas-cobros', '/apps/board', '/sofono',
        '/cotizaciones', '/dashboardClient_search', '/dashboardClient_get',
        '/resumen-cliente',
      ];
      const isContextAware = CONTEXT_AWARE.some(p => this.$route.path.startsWith(p));
      if (!isContextAware) {
        this.$router.push({ path: '/resumen-cliente', query: { rut: client.rut } }).catch(() => {});
      }
      this.close();
    },

    clientInitials(c) {
      const name = c.nombre || c.username || '';
      return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
    },

    /**
     * Chip que indica el estado del servicio que coincidió con el dato buscado
     * (IP/dominio). Solo se muestra para habilitado (1) y suspendido (2); para el
     * resto de estados o búsquedas por nombre/RUT no hay marca.
     */
    matchBadge(c) {
      const estado = Number(c && c.match_estado);
      if (estado === 1) return { text: 'Servicio activo', cls: 'gs-match-activo' };
      if (estado === 2) return { text: 'Servicio suspendido', cls: 'gs-match-suspendido' };
      return null;
    },

  },
};
</script>

<style scoped>
/* ── Overlay ──────────────────────────────────────────── */
.gs-overlay {
  position: fixed;
  inset: 0;
  z-index: 9900;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  backdrop-filter: blur(4px);
}

/* ── Box ──────────────────────────────────────────────── */
.gs-box {
  width: 100%;
  max-width: 580px;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.5);
}
.gs-dark  { background: #0f1e32; border: 1px solid rgba(255,255,255,0.10); color: #dde4ef; }
.gs-light { background: #ffffff; border: 1px solid rgba(0,0,0,0.08);       color: #1a1a2e; box-shadow: 0 24px 60px rgba(0,0,0,0.15); }

/* ── Input row ────────────────────────────────────────── */
.gs-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  flex-shrink: 0;
}
.gs-light .gs-input-row { border-bottom-color: rgba(0,0,0,0.07); }

.gs-icon { font-size: 19px; color: #6b7a9a; flex-shrink: 0; }
.gs-light .gs-icon { color: #94a3b8; }

.gs-breadcrumb {
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--brand, #0096c7);
  flex-shrink: 0;
}

.gs-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14.5px;
  font-family: inherit;
  color: inherit;
  min-width: 0;
}
.gs-input::placeholder { color: #6b7a9a; }
.gs-light .gs-input::placeholder { color: #94a3b8; }

.gs-esc {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  color: #6b7a9a;
  font-family: inherit;
  flex-shrink: 0;
}
.gs-light .gs-esc { background: #e8eaed; border-color: rgba(0,0,0,0.22); color: #374151; }

/* ── Spinner ──────────────────────────────────────────── */
.gs-spinner { display: flex; gap: 3px; align-items: center; flex-shrink: 0; }
.gs-spinner span { width: 5px; height: 5px; border-radius: 50%; background: #0096c7; animation: gs-pulse 1s ease-in-out infinite; }
.gs-spinner span:nth-child(2) { animation-delay: .15s; }
.gs-spinner span:nth-child(3) { animation-delay: .3s; }
@keyframes gs-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.3; transform:scale(.7); } }

/* ── Results ──────────────────────────────────────────── */
.gs-results {
  max-height: 380px;
  overflow-y: auto;
  padding: 4px 6px;
}

.gs-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7a9a;
  padding: 8px 10px 3px;
}
.gs-light .gs-section-label { color: #94a3b8; }

.gs-result {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font-family: inherit;
  font-size: 13px;
  transition: background 80ms;
}
.gs-result:hover,
.gs-result--sel { background: rgba(0,150,199,0.12); }
.gs-light .gs-result:hover,
.gs-light .gs-result--sel { background: rgba(0,120,200,0.08); }

.gs-r-icon { font-size: 16px; color: #6b7a9a; flex-shrink: 0; }
.gs-light .gs-r-icon { color: #94a3b8; }
.gs-result--sel .gs-r-icon,
.gs-result:hover .gs-r-icon { color: var(--brand, #0096c7); }

.gs-r-title  { flex: 1; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gs-r-module { font-size: 11px; color: #6b7a9a; white-space: nowrap; flex-shrink: 0; }
.gs-light .gs-r-module { color: #94a3b8; }

/* Ticket-specific */
.gs-r-id     { font-size: 11px; font-weight: 700; color: #0096c7; flex-shrink: 0; min-width: 36px; }
.gs-r-client { font-size: 11px; color: #6b7a9a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; }
.gs-light .gs-r-client { color: #94a3b8; }
.gs-r-badge  { font-size: 10.5px; font-weight: 500; padding: 2px 7px; border-radius: 5px; flex-shrink: 0; }
.gs-s0 { background: rgba(107,114,128,.15); color: #9ca3af; }
.gs-s1 { background: rgba(59,130,246,.15);  color: #60a5fa; }
.gs-s2 { background: rgba(16,185,129,.15);  color: #34d399; }
.gs-s3 { background: rgba(245,158,11,.15);  color: #fbbf24; }
.gs-s4 { background: rgba(239,68,68,.12);   color: #f87171; }
.gs-light .gs-s0 { color: #6b7280; } .gs-light .gs-s1 { color: #2563eb; }
.gs-light .gs-s2 { color: #059669; } .gs-light .gs-s3 { color: #d97706; }
.gs-light .gs-s4 { color: #dc2626; }

/* Tab hint inside result row */
.gs-tab-hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: #6b7a9a;
  white-space: nowrap;
  flex-shrink: 0;
}
.gs-tab-hint kbd {
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.15);
  font-family: inherit;
}
.gs-light .gs-tab-hint kbd { background: #e8eaed; border-color: rgba(0,0,0,0.22); color: #374151; }

/* ── Param mode body ──────────────────────────────────── */
.gs-param-body {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.gs-light .gs-param-body { border-bottom-color: rgba(0,0,0,0.06); }
.gs-param-icon { font-size: 28px; color: var(--brand, #0096c7); flex-shrink: 0; margin-top: 2px; }
.gs-param-info { display: flex; flex-direction: column; gap: 4px; }
.gs-param-name { font-size: 14px; font-weight: 600; }
.gs-param-desc { font-size: 12px; color: #6b7a9a; line-height: 1.45; }
.gs-param-desc em { font-style: normal; font-weight: 600; color: inherit; }
.gs-param-desc kbd {
  font-size: 10px; padding: 1px 5px; border-radius: 3px;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.15);
  font-family: inherit;
}
.gs-light .gs-param-desc { color: #94a3b8; }
.gs-light .gs-param-desc kbd { background: #e8eaed; border-color: rgba(0,0,0,0.22); color: #374151; }

/* ── Empty ────────────────────────────────────────────── */
.gs-empty {
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7a9a;
  text-align: center;
}

/* ── Footer hint ──────────────────────────────────────── */
.gs-hint {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 9px 14px;
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.gs-light .gs-hint { border-top-color: rgba(0,0,0,0.06); }
.gs-hint-shortcuts { display: flex; gap: 4px; align-items: center; font-size: 11px; color: #6b7a9a; }
.gs-hint-shortcuts kbd {
  font-size: 10px; padding: 1px 5px; border-radius: 3px;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.15);
  font-family: inherit;
}
.gs-light .gs-hint-shortcuts kbd { background: #e8eaed; border-color: rgba(0,0,0,0.22); color: #374151; }

/* ── Search-by inline select (param mode) ────────────── */
.pal-by-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  padding: 4px 8px;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  font-family: inherit;
}
.pal-by-select option { background: #1c1c1e; color: #fff; }
.gs-light .pal-by-select {
  background: rgba(0, 0, 0, 0.06);
  border-color: rgba(0, 0, 0, 0.14);
  color: #1a1a2e;
}
.gs-light .pal-by-select option { background: #fff; color: #1a1a2e; }

/* ── Client results ──────────────────────────────────── */
.gs-result--client {
  cursor: default;
  gap: 10px;
}
.gs-result--client:hover { background: transparent; }

.gs-client-avatar {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0fa3a3;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gs-cv-avatar {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #0fa3a3;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gs-client-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.gs-client-meta {
  font-size: 10.5px;
  opacity: 0.55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Match badge (estado del servicio buscado por IP/dominio) ── */
.gs-match-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.01em;
  vertical-align: middle;
  white-space: nowrap;
}
.gs-match-activo {
  background: rgba(16, 185, 129, 0.16);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.35);
}
.gs-match-suspendido {
  background: rgba(245, 158, 11, 0.16);
  color: #fbbf24;
  border: 1px solid rgba(245, 158, 11, 0.35);
}
.gs-light .gs-match-activo { color: #059669; }
.gs-light .gs-match-suspendido { color: #d97706; }

.gs-ctx-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid rgba(15, 163, 163, 0.4);
  background: transparent;
  color: #0fa3a3;
  font-size: 10.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.gs-ctx-btn:hover {
  background: rgba(15, 163, 163, 0.15);
  border-color: #0fa3a3;
}

.gs-ctx-btn--active {
  background: rgba(15, 163, 163, 0.18);
  border-color: #0fa3a3;
}

.gs-ctx-btn .mdi { font-size: 12px; }

.gs-clients-loading {
  display: inline-flex;
  gap: 3px;
  align-items: center;
  margin-left: 6px;
}

.gs-dot-spin {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #0fa3a3;
  animation: gs-pulse 1s ease-in-out infinite;
}
.gs-dot-spin:nth-child(2) { animation-delay: 0.2s; }
.gs-dot-spin:nth-child(3) { animation-delay: 0.4s; }

@keyframes gs-pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50%       { opacity: 1;   transform: scale(1); }
}

.gs-clients-empty {
  padding: 8px 14px 6px;
  font-size: 12px;
  color: #6b7a9a;
}
.gs-clients-empty em { font-style: normal; font-weight: 600; }

/* ── Transitions ──────────────────────────────────────── */
.gs-fade-enter-active, .gs-fade-leave-active   { transition: opacity 150ms ease; }
.gs-fade-enter, .gs-fade-leave-to             { opacity: 0; }
.gs-slide-enter-active, .gs-slide-leave-active { transition: opacity 150ms ease, transform 150ms ease; }
.gs-slide-enter, .gs-slide-leave-to           { opacity: 0; transform: translateY(-10px) scale(0.97); }
</style>
